import type { ThemeConfig } from '@yimoka/antd';

import { getCurrentLanguage } from '@/locales/language';
import { tenantWebTheme } from '@/theme/app-theme';

import { useOptionalRealmProfile } from './context';
import type { IRealmProfileSnapshot } from './types';

/** getProfileAsset 按版本内 manifest 解析品牌素材。 */
export function getProfileAsset(profile: IRealmProfileSnapshot | undefined, key?: string) {
  if (!profile || !key) return undefined;
  return profile.webProfile.assetManifest.find(item => item.key === key);
}

/** resolveRealmTitle 按当前语言读取渠道标题并回退到版本主标题。 */
export function resolveRealmTitle(profile?: IRealmProfileSnapshot): string | undefined {
  if (!profile) return undefined;
  return profile.titleI18n[getCurrentLanguage()] || profile.webProfile.brand.shortTitle || profile.title;
}

/** createTenantWebRealmTheme 在保留原 tenant-web 组件主题的基础上覆盖渠道 token。 */
export function createTenantWebRealmTheme(profile?: IRealmProfileSnapshot): ThemeConfig {
  const realmTheme = profile?.webProfile.theme;
  if (!realmTheme) return tenantWebTheme;
  return {
    ...tenantWebTheme,
    token: {
      ...tenantWebTheme.token,
      borderRadius: realmTheme.borderRadius ?? tenantWebTheme.token?.borderRadius,
      colorError: realmTheme.errorColor ?? tenantWebTheme.token?.colorError,
      colorInfo: realmTheme.infoColor ?? tenantWebTheme.token?.colorInfo,
      colorPrimary: realmTheme.primaryColor ?? tenantWebTheme.token?.colorPrimary,
      colorSuccess: realmTheme.successColor ?? tenantWebTheme.token?.colorSuccess,
      colorWarning: realmTheme.warningColor ?? tenantWebTheme.token?.colorWarning,
      fontFamily: realmTheme.fontFamilyToken === 'system'
        ? '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        : tenantWebTheme.token?.fontFamily,
    },
  };
}

/** useRealmBrand 为全站壳层提供同一渠道标题与 Logo，并允许配置未就绪时使用原值。 */
export function useRealmBrand(fallbackTitle = '', fallbackLogo = '/assets/logo.png') {
  const profile = useOptionalRealmProfile()?.profile;
  const logo = getProfileAsset(profile, profile?.webProfile.brand.logoAssetKey);
  return {
    configured: Boolean(profile),
    logoAlt: logo?.alt || resolveRealmTitle(profile) || fallbackTitle,
    logoURL: logo?.src || fallbackLogo,
    shortTitle: profile?.webProfile.brand.shortTitle || resolveRealmTitle(profile) || fallbackTitle,
    title: resolveRealmTitle(profile) || fallbackTitle,
  };
}
